import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { cx, formatToCurrency } from "shared/utils";
import { DateTime } from "luxon";
import qs from "qs";
import { TruncateWithTooltip } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowUp02Icon,
  Sorting01Icon,
  Sorting02Icon
} from "@hugeicons-pro/core-solid-rounded";
import { BankStatements } from "../../types";
import { isValidBankStatement } from "../../utils";
import {
  BankStatementTransaction,
  SelectedTransaction,
  TransactionRowLike
} from "./types";

export const ITEMS_PER_PAGE = Number(
  window.localStorage.getItem("per_page") || 10
);
export const INITIAL_ASSIGNED_PER_PAGE = 1000;
export const INIT_SORTING: SortingState = [{ id: "amount", desc: true }];

export const columnHelper = createColumnHelper<TransactionRowLike>();

export const serializeParams = (params: unknown) =>
  qs.stringify(params, { arrayFormat: "brackets", skipNulls: true });

export const toSelected = (
  tx: BankStatementTransaction
): SelectedTransaction => ({
  id: tx.id,
  description: tx.description,
  date: tx.date,
  amount_cents: tx.amount_cents ?? 0,
  currency_code: tx.currency_code
});

export const buildTxUrl = (applicationId: number, verificationId: number) =>
  `/portal/v1/applications/${applicationId}/verifications/${verificationId}/bank_statement_transactions`;

export const getSharedColumns = (
  isDisabledFn: (row: TransactionRowLike) => boolean,
  isSelectedFn?: (row: TransactionRowLike) => boolean
) => [
  columnHelper.accessor((row) => row.description, {
    id: "description",
    header: "Name",
    size: 100,
    meta: {
      sortIcon: (
        <HugeiconsIcon icon={ArrowUp02Icon} size={14} className="shrink-0" />
      )
    },
    cell: ({ row }) => {
      const isDisabled = isDisabledFn(row.original);
      const isSelected = isSelectedFn?.(row.original) ?? false;
      const description = row.original.description || "-";
      return (
        <TruncateWithTooltip
          content={description}
          maxWidth="200px"
          tooltipClassName="z-[1000] border-white/40"
          className={cx(
            "text-sm",
            isDisabled ? "text-content-100" : "text-content-1000",
            isSelected && !isDisabled ? "font-bold" : ""
          )}
        />
      );
    }
  }),
  columnHelper.accessor((row) => row.date, {
    id: "date fixed_width",
    header: "Date",
    size: 110,
    meta: {
      sortIcon: (
        <HugeiconsIcon icon={ArrowUp02Icon} size={14} className="shrink-0" />
      )
    },
    cell: ({ row }) => {
      const date = row.original.date;
      return (
        <p className="text-sm text-content-1000 whitespace-nowrap">
          {date ? DateTime.fromISO(date).toFormat("MMM d, yyyy") : "-"}
        </p>
      );
    }
  }),
  columnHelper.accessor((row) => row.amount_cents, {
    id: "amount right-align fixed_width",
    header: "Amount",
    size: 100,
    meta: {
      sortIcon: (desc?: boolean) => (
        <HugeiconsIcon
          icon={desc ? Sorting01Icon : Sorting02Icon}
          size={14}
          className="shrink-0"
        />
      )
    },
    cell: ({ row }) => {
      const { amount_cents, currency_code } = row.original;
      const formattedValue =
        amount_cents != null
          ? formatToCurrency({
              cents: amount_cents,
              currency: currency_code ?? "USD"
            })
          : "-";

      return (
        <p className="text-sm font-bold text-right text-success-700">
          {formattedValue}
        </p>
      );
    }
  })
];

export const getAvailableBankStatements = (
  allBankStatements?: BankStatements
) => {
  if (!allBankStatements) return [];
  return Object.entries(allBankStatements)
    .filter(([, statement]) => isValidBankStatement(statement))
    .map(([id, statement]) => ({ id: Number(id), statement }));
};

export const belongsToOtherIncome = (
  row: { income_id: number | null },
  incomeId?: number
) => Boolean(row.income_id && row.income_id !== incomeId);
